import './CreateDocumentMenu.scss';
import React, { FC, memo, RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import isEqual from 'react-fast-compare';
import { createPortal } from 'react-dom';
import { CloseButton, FocusTrap, Popover } from '@mantine/core';
import { useReducedMotion } from '@mantine/hooks';
import clsx from 'clsx';
import { MdOpenInNew, MdOutlineSearch, MdOutlineVerifiedUser, MdStar, MdStarBorder } from 'react-icons/md';
import { useLocalStorageState } from '@srm/ui/hooks/useLocalStorageState';
import { IBaseAction } from '@models/actions/IBaseAction';
import type { IDocumentDraft } from '@models/documentDraft/IDocumentDraft';
import { DocumentDraftService } from '@services/DocumentDraftService';
import DocumentDraftList from '@organisms/document/DocumentDraftList';
import { plural } from '@srm/ui/lib/pluralize';
import Button from '@srm/ui/button';
import Tooltip from '@srm/ui/tooltip';
import ActionKeyButton from '@atoms/ActionKeyButton/ActionKeyButton';
import Skeleton from '@srm/ui/skeleton';
import { ACTION_KEY_TOOLTIP_Z_INDEX } from '@srm/ui/lib/zIndex';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { buildMenuSections, ICreateDocMenuItem, ICreateDocMenuSection } from './buildMenuSections';
import { DEFAULT_ACTION_ICON, DEFAULT_SECTION_ICON, resolveMenuIcon } from './menuIcons';
import {
    DEFAULT_QUICK_STATE,
    IQuickAccessState,
    QUICK_SECTION_HUE,
    QUICK_SECTION_ID,
    registerActionUse,
    selectQuickActions,
    togglePinned,
} from './quickAccess';

export interface ICreateDocumentMenuProps {
    /** Кнопка-триггер: из её геометрии модалка вырастает и в неё же схлопывается. */
    anchorRef: RefObject<HTMLElement | null>;
    opened: boolean;
    isLoading?: boolean;
    actions: IBaseAction[];
    onClose: () => void;
    /** Запуск действия; вызывается после закрытия меню. */
    onAction: (action: IBaseAction) => void;
    /** Dev-режим: кнопка копирования ключа действия на карточке. */
    showActionKeys?: boolean;
    /** Черновики включены (autoSaveDocument): на карточке действия — «N черновиков · продолжить». */
    draftsEnabled?: boolean;
    /** Открыть черновик из списка на карточке; вызывается после закрытия меню. */
    onOpenDraft?: (draft: IDocumentDraft) => void;
    /** Открыт ли черновик сейчас в карточке (по адресу страницы): его из списка не удалить. */
    isDraftOpen?: (draft: IDocumentDraft) => boolean;
}

interface IVisibleSection extends ICreateDocMenuSection {
    /** Полное число действий раздела до фильтра поиска. */
    total: number;
}

const MORPH_EASE = 'cubic-bezier(0.32, 0.72, 0.28, 1)';
const OPEN_DURATION = 380;
const CLOSE_DURATION = 300;
/** Линия (доля высоты ленты), по пересечению которой раздел считается активным. */
const ACTIVE_SECTION_LINE = 0.35;

const countLabel = (visible: number, total: number): string =>
    visible === total ? `${total} ${plural(total, ['действие', 'действия', 'действий'])}` : `${visible} из ${total}`;

const draftsLabel = (count: number): string => `${count} ${plural(count, ['черновик', 'черновика', 'черновиков'])}`;

const canAnimate = (el: HTMLElement | null): el is HTMLElement => typeof el?.animate === 'function';

export const CreateDocumentMenu: FC<ICreateDocumentMenuProps> = ({
    anchorRef,
    opened: isOpen,
    isLoading,
    actions,
    onClose,
    onAction,
    showActionKeys,
    draftsEnabled,
    onOpenDraft,
    isDraftOpen,
}) => {
    // Модалка живёт дольше isOpen: после isOpen=false доигрывает обратный морф и лишь
    // затем размонтируется. mounted — рендер портала, closing — фаза обратного морфа.
    const [mounted, setMounted] = useState(false);
    const [closing, setClosing] = useState(false);
    const [query, setQuery] = useState('');
    const [activeSection, setActiveSection] = useState<string>();
    const [topOffset, setTopOffset] = useState<number>();
    // Незавершённые черновики пользователя по действию создания: грузятся один раз на открытие.
    const [drafts, setDrafts] = useState<IDocumentDraft[]>([]);
    const [openDraftsKey, setOpenDraftsKey] = useState<string | null>(null);
    const draftsRequestRef = useRef(0);

    const dialogRef = useRef<HTMLDivElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);
    const paneRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    const reduceMotion = useReducedMotion();

    // Быстрый доступ: закреплённые + frecency, состояние per-пользователь в localStorage.
    // Ключи резолвятся против текущего списка actions — удалённые из конфига или
    // скрытые правами действия в секцию не попадают (см. quickAccess.ts).
    const username = useTypedSelector((state) => state.auth.user.username);
    const [quickState, setQuickState] = useLocalStorageState<IQuickAccessState>(
        `user-${username}.createDocQuick`,
        DEFAULT_QUICK_STATE,
    );

    const sections = useMemo(() => buildMenuSections(actions), [actions]);

    const draftsByAction = useMemo(() => {
        const map = new Map<string, IDocumentDraft[]>();
        drafts.forEach((draft) => {
            if (!draft.actionId) return;
            map.set(draft.actionId, [...(map.get(draft.actionId) ?? []), draft]);
        });
        return map;
    }, [drafts]);
    const quickActions = useMemo(() => selectQuickActions(quickState, actions, Date.now()), [quickState, actions]);
    /** Мета плитки быстрого доступа: иконка и оттенок родного раздела действия. */
    const actionMeta = useMemo(() => {
        const map = new Map<string, { icon?: string; color: string; description?: string }>();
        sections.forEach((section) =>
            section.items.forEach((item) =>
                map.set(item.action.key, { icon: item.icon, color: section.color, description: item.description }),
            ),
        );
        return map;
    }, [sections]);

    const needle = query.trim().toLowerCase();
    const visibleSections = useMemo<IVisibleSection[]>(() => {
        const withTotals = sections.map((section) => ({ ...section, total: section.items.length }));
        if (!needle) {
            return withTotals;
        }
        return withTotals
            .map((section) => ({ ...section, items: section.items.filter((i) => i.searchText.includes(needle)) }))
            .filter((section) => section.items.length > 0);
    }, [sections, needle]);

    useEffect(() => {
        if (isOpen) {
            setMounted(true);
            setClosing(false);
            setQuery('');
            setTopOffset(anchorRef.current ? anchorRef.current.getBoundingClientRect().bottom + 10 : undefined);
            setOpenDraftsKey(null);
            if (draftsEnabled) {
                const requestId = ++draftsRequestRef.current;
                void DocumentDraftService.list()
                    .then((items) => {
                        if (draftsRequestRef.current !== requestId) return;
                        // те же черновики не перерисовывают все карточки посреди анимации открытия
                        setDrafts((prev) => (isEqual(prev, items) ? prev : items));
                    })
                    .catch(() => {
                        if (draftsRequestRef.current === requestId) setDrafts([]);
                    });
            }
        } else if (mounted) {
            setClosing(true);
        }
        // mounted намеренно не в зависимостях: эффект реагирует только на isOpen.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    /** Кадры морфа: от прямоугольника кнопки-триггера к финальному прямоугольнику модалки. */
    const morphKeyframes = useCallback((): Keyframe[] | undefined => {
        const anchor = anchorRef.current;
        const dialog = dialogRef.current;
        if (!anchor || !dialog) {
            return undefined;
        }
        const from = anchor.getBoundingClientRect();
        const to = dialog.getBoundingClientRect();
        if (!to.width || !to.height) {
            return undefined;
        }
        return [
            {
                transform: `translate(${from.left - to.left}px, ${from.top - to.top}px) scale(${from.width / to.width}, ${from.height / to.height})`,
                opacity: 0.35,
                borderRadius: '10px',
            },
            { transform: 'translate(0, 0) scale(1, 1)', opacity: 1, borderRadius: '16px' },
        ];
    }, [anchorRef]);

    // Открытие: скролл в начало, фокус в поиск, прямой морф.
    useLayoutEffect(() => {
        if (!mounted || closing) {
            return;
        }
        paneRef.current?.scrollTo({ top: 0 });
        setActiveSection(undefined);
        searchRef.current?.focus({ preventScroll: true });

        const dialog = dialogRef.current;
        if (reduceMotion || !canAnimate(dialog)) {
            return;
        }
        const frames = morphKeyframes();
        if (!frames) {
            return;
        }
        dialog.animate(frames, { duration: OPEN_DURATION, easing: MORPH_EASE });
        if (canAnimate(bodyRef.current)) {
            bodyRef.current.animate(
                [
                    { opacity: 0, transform: 'translateY(10px)' },
                    { opacity: 1, transform: 'translateY(0)' },
                ],
                { duration: 300, delay: 130, easing: 'ease-out', fill: 'backwards' },
            );
        }
    }, [mounted, closing, reduceMotion, morphKeyframes]);

    // Закрытие: обратный морф в кнопку, затем размонтирование и возврат фокуса.
    useLayoutEffect(() => {
        if (!closing) {
            return;
        }
        const finish = () => {
            setMounted(false);
            setClosing(false);
            const anchor = anchorRef.current;
            if (anchor?.contains(document.activeElement) === false) {
                anchor.querySelector<HTMLElement>('button')?.focus({ preventScroll: true });
            }
        };
        const dialog = dialogRef.current;
        const frames = !reduceMotion && canAnimate(dialog) ? morphKeyframes() : undefined;
        if (!frames || !dialog) {
            finish();
            return;
        }
        const animation = dialog.animate([...frames].reverse(), {
            duration: CLOSE_DURATION,
            easing: MORPH_EASE,
            fill: 'forwards',
        });
        animation.onfinish = finish;
        animation.oncancel = finish;
        // Скрытая вкладка замораживает WAAPI-таймлайн (onfinish не наступает) —
        // страховочный таймер гарантирует, что закрытие всё равно завершится.
        const fallbackTimer = window.setTimeout(() => {
            animation.cancel();
        }, CLOSE_DURATION + 200);
        return () => window.clearTimeout(fallbackTimer);
    }, [closing, reduceMotion, morphKeyframes, anchorRef]);

    useEffect(() => {
        if (!mounted || closing) {
            return;
        }
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                onClose();
            } else if (event.key === '/' && document.activeElement !== searchRef.current) {
                event.preventDefault();
                searchRef.current?.focus();
            }
        };
        document.addEventListener('keydown', onKeyDown, true);
        return () => document.removeEventListener('keydown', onKeyDown, true);
    }, [mounted, closing, onClose]);

    // Активный раздел в левой навигации следует за скролом ленты: побеждает последний
    // раздел, чей верх пересёк контрольную линию (или первый, пока никто не пересёк).
    const syncActiveSection = useCallback(() => {
        const pane = paneRef.current;
        if (!pane) {
            return;
        }
        const line = pane.scrollTop + pane.clientHeight * ACTIVE_SECTION_LINE;
        let current: string | undefined;
        pane.querySelectorAll<HTMLElement>('[data-menu-section]').forEach((el) => {
            if (current === undefined || el.offsetTop <= line) {
                current = el.dataset.menuSection;
            }
        });
        setActiveSection(current);
    }, []);

    useEffect(() => {
        if (mounted) {
            syncActiveSection();
        }
    }, [mounted, visibleSections, syncActiveSection]);

    const onPaneScroll = useCallback(() => requestAnimationFrame(syncActiveSection), [syncActiveSection]);

    const scrollToSection = useCallback(
        (name: string) => {
            const pane = paneRef.current;
            const target = pane?.querySelector<HTMLElement>(`[data-menu-section="${CSS.escape(name)}"]`);
            if (!pane || !target) {
                return;
            }
            pane.scrollTo({ top: target.offsetTop, behavior: reduceMotion ? 'auto' : 'smooth' });
        },
        [reduceMotion],
    );

    const runAction = useCallback(
        (action: IBaseAction) => {
            setQuickState((prev) => registerActionUse(prev, action.key, Date.now()));
            onClose();
            onAction(action);
        },
        [onAction, onClose, setQuickState],
    );

    const onTogglePin = useCallback(
        (key: string) => {
            setQuickState((prev) => togglePinned(prev, key));
        },
        [setQuickState],
    );

    const runActionInNewTab = useCallback(
        (action: IBaseAction) => {
            runAction({ ...action, options: { ...(action.options ?? {}), newTab: true } });
        },
        [runAction],
    );

    const highlightMatch = useCallback(
        (name: string) => {
            if (!needle) {
                return name;
            }
            const start = name.toLowerCase().indexOf(needle);
            if (start < 0) {
                return name;
            }
            const end = start + needle.length;
            return (
                <>
                    {name.slice(0, start)}
                    <mark>{name.slice(start, end)}</mark>
                    {name.slice(end)}
                </>
            );
        },
        [needle],
    );

    if (!mounted) {
        return null;
    }

    const showSkeleton = Boolean(isLoading) && actions.length === 0;
    const isEmpty = !showSkeleton && actions.length === 0;
    const nothingFound = !showSkeleton && !isEmpty && visibleSections.length === 0;

    const openDraft = (draft: IDocumentDraft) => {
        setOpenDraftsKey(null);
        onClose();
        onOpenDraft?.(draft);
    };

    const deleteDraft = async (draft: IDocumentDraft) => {
        await DocumentDraftService.remove(draft.id);
        setDrafts((prev) => prev.filter((item) => item.id !== draft.id));
    };

    const renderCard = (item: ICreateDocMenuItem) => {
        const Icon = resolveMenuIcon(item.icon, DEFAULT_ACTION_ICON);
        const { action } = item;
        const isPinned = quickState.pinned.includes(action.key);
        const actionDrafts = draftsEnabled ? (draftsByAction.get(action.key) ?? []) : [];

        return (
            <div
                key={action.key}
                className={clsx(
                    'rf-create-doc-menu__card',
                    actionDrafts.length > 0 && 'rf-create-doc-menu__card--with-drafts',
                )}
            >
                <button
                    type="button"
                    className="rf-create-doc-menu__card-main"
                    aria-label={action.displayName}
                    onClick={() => runAction(action)}
                >
                    <span className="rf-create-doc-menu__card-tile" aria-hidden="true">
                        <Icon size={22} />
                    </span>
                    <span className="rf-create-doc-menu__card-name">{highlightMatch(action.displayName)}</span>
                    {item.description && <span className="rf-create-doc-menu__card-desc">{item.description}</span>}
                </button>
                {actionDrafts.length > 0 && (
                    <Popover
                        opened={openDraftsKey === action.key}
                        onChange={(opened) => setOpenDraftsKey(opened ? action.key : null)}
                        position="bottom-start"
                        shadow="md"
                        radius={12}
                        width={360}
                        zIndex={ACTION_KEY_TOOLTIP_Z_INDEX}
                        withinPortal
                        middlewares={{ flip: true, shift: true, size: { padding: 8 } }}
                    >
                        <Popover.Target>
                            <button
                                type="button"
                                className="rf-create-doc-menu__card-drafts"
                                aria-haspopup="dialog"
                                aria-expanded={openDraftsKey === action.key}
                                onClick={() => setOpenDraftsKey((key) => (key === action.key ? null : action.key))}
                                data-testid={`create-doc-menu-drafts-${action.key}`}
                            >
                                <span className="rf-create-doc-menu__card-drafts-dot" aria-hidden="true" />
                                {draftsLabel(actionDrafts.length)} · продолжить
                            </button>
                        </Popover.Target>
                        <Popover.Dropdown className="rf-create-doc-menu__drafts-panel">
                            <DocumentDraftList
                                drafts={actionDrafts}
                                documentTitle={action.displayName}
                                onOpenDraft={openDraft}
                                onDeleteDraft={deleteDraft}
                                activeDraftId={actionDrafts.find((draft) => isDraftOpen?.(draft))?.id}
                            />
                        </Popover.Dropdown>
                    </Popover>
                )}
                <span
                    className={clsx(
                        'rf-create-doc-menu__card-tools',
                        isPinned && 'rf-create-doc-menu__card-tools--pinned',
                    )}
                >
                    <Tooltip
                        position="bottom"
                        background="black"
                        zIndex={ACTION_KEY_TOOLTIP_Z_INDEX}
                        label={isPinned ? 'Открепить' : 'Закрепить в быстром доступе'}
                    >
                        <Button
                            variant="icon"
                            size="auto"
                            className={clsx(isPinned && 'rf-create-doc-menu__pin-on')}
                            aria-label={isPinned ? 'Открепить' : 'Закрепить в быстром доступе'}
                            onClick={() => onTogglePin(action.key)}
                            startAdornment={isPinned ? <MdStar size="18" /> : <MdStarBorder size="18" />}
                        />
                    </Tooltip>
                    {action.options?.showOpenInNewTabButton && (
                        <Tooltip
                            position="bottom"
                            background="black"
                            zIndex={ACTION_KEY_TOOLTIP_Z_INDEX}
                            label="Открыть в новой вкладке"
                        >
                            <Button
                                variant="icon"
                                size="auto"
                                aria-label="Открыть в новой вкладке"
                                onClick={() => runActionInNewTab(action)}
                                startAdornment={<MdOpenInNew size="18" />}
                            />
                        </Tooltip>
                    )}
                    {showActionKeys && <ActionKeyButton type={action.type} actionKey={action.key} iconSize={18} />}
                </span>
            </div>
        );
    };

    const renderSection = (section: IVisibleSection) => (
        <section
            key={section.name}
            className="rf-create-doc-menu__section"
            data-menu-section={section.name}
            style={{ '--menu-hue': section.color } as React.CSSProperties}
        >
            <div className="rf-create-doc-menu__section-head">
                <div>
                    <div className="rf-create-doc-menu__section-eyebrow">Раздел</div>
                    <h3 className="rf-create-doc-menu__section-name">{section.name}</h3>
                </div>
                <span className="rf-create-doc-menu__section-count">
                    {countLabel(section.items.length, section.total)}
                </span>
            </div>
            <div className="rf-create-doc-menu__cards">{section.items.map(renderCard)}</div>
        </section>
    );

    const renderRailItem = (section: IVisibleSection) => {
        const SectionIcon = resolveMenuIcon(section.icon, DEFAULT_SECTION_ICON);
        return (
            <button
                key={section.name}
                type="button"
                aria-label={section.name}
                className={clsx(
                    'rf-create-doc-menu__rail-item',
                    section.name === activeSection && 'rf-create-doc-menu__rail-item--active',
                )}
                style={{ '--menu-hue': section.color } as React.CSSProperties}
                onClick={() => scrollToSection(section.name)}
            >
                <SectionIcon size={18} />
                <span className="rf-create-doc-menu__rail-label">{section.name}</span>
                <span className="rf-create-doc-menu__rail-count">{section.items.length}</span>
            </button>
        );
    };

    // Быстрый доступ: при поиске секция прячется — ищут то, чего в ней нет.
    const showQuick = !needle && !showSkeleton && quickActions.length > 0;

    const renderQuickTile = (action: IBaseAction) => {
        const meta = actionMeta.get(action.key);
        const Icon = resolveMenuIcon(meta?.icon, DEFAULT_ACTION_ICON);
        const isPinned = quickState.pinned.includes(action.key);
        return (
            <div
                key={action.key}
                className={clsx('rf-create-doc-menu__qt', isPinned && 'rf-create-doc-menu__qt--pinned')}
                style={{ '--menu-hue': meta?.color ?? QUICK_SECTION_HUE } as React.CSSProperties}
            >
                <button
                    type="button"
                    className="rf-create-doc-menu__qt-main"
                    title={meta?.description}
                    onClick={() => runAction(action)}
                >
                    <span className="rf-create-doc-menu__qt-tile" aria-hidden="true">
                        <Icon size={17} />
                    </span>
                    <span className="rf-create-doc-menu__qt-name">{action.displayName}</span>
                </button>
                <button
                    type="button"
                    className="rf-create-doc-menu__qt-pin"
                    aria-label={isPinned ? 'Открепить' : 'Закрепить'}
                    title={isPinned ? 'Открепить' : 'Закрепить'}
                    onClick={() => onTogglePin(action.key)}
                >
                    {isPinned ? <MdStar size={15} /> : <MdStarBorder size={15} />}
                </button>
            </div>
        );
    };

    const renderQuickSection = () => (
        <section
            className="rf-create-doc-menu__section"
            data-menu-section={QUICK_SECTION_ID}
            style={{ '--menu-hue': QUICK_SECTION_HUE } as React.CSSProperties}
        >
            <div className="rf-create-doc-menu__section-head">
                <div>
                    <h3 className="rf-create-doc-menu__section-name">Быстрый доступ</h3>
                </div>
                <span className="rf-create-doc-menu__section-hint">звезда на карточке закрепляет действие</span>
            </div>
            <div className="rf-create-doc-menu__quick-row">{quickActions.map(renderQuickTile)}</div>
        </section>
    );

    const renderQuickRailItem = () => (
        <button
            type="button"
            className={clsx(
                'rf-create-doc-menu__rail-item',
                activeSection === QUICK_SECTION_ID && 'rf-create-doc-menu__rail-item--active',
            )}
            style={{ '--menu-hue': QUICK_SECTION_HUE } as React.CSSProperties}
            onClick={() => scrollToSection(QUICK_SECTION_ID)}
        >
            <MdStarBorder size={18} />
            <span className="rf-create-doc-menu__rail-label">Быстрый доступ</span>
            <span className="rf-create-doc-menu__rail-count">{quickActions.length}</span>
        </button>
    );

    const renderSkeleton = () => (
        <div className="rf-create-doc-menu__section" aria-busy="true" aria-label="Загрузка действий">
            <div className="rf-create-doc-menu__section-head">
                <Skeleton width={160} height={24} radius={6} />
            </div>
            <div className="rf-create-doc-menu__cards">
                {Array.from({ length: 6 }, (_, i) => (
                    <div key={i} className="rf-create-doc-menu__card rf-create-doc-menu__card--skeleton">
                        <Skeleton width={44} height={44} radius={12} />
                        <div>
                            <Skeleton width="60%" height={14} radius={4} mb={8} />
                            <Skeleton width="85%" height={11} radius={4} />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    return createPortal(
        <div className={clsx('rf-create-doc-menu', closing && 'rf-create-doc-menu--closing')}>
            <div className="rf-create-doc-menu__scrim" onClick={onClose} aria-hidden="true" />
            {/* FocusTrap затирает ref ребёнка через cloneElement — свой ref передаём его innerRef. */}
            <FocusTrap active={!closing} innerRef={dialogRef}>
                <div
                    className="rf-create-doc-menu__dialog"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Создать документ"
                    style={
                        topOffset !== undefined
                            ? ({ top: topOffset, '--cdm-top': `${topOffset}px` } as React.CSSProperties)
                            : undefined
                    }
                >
                    <header className="rf-create-doc-menu__head">
                        <h2 className="rf-create-doc-menu__title">Создать документ</h2>
                        <div className="rf-create-doc-menu__search">
                            <MdOutlineSearch size={18} aria-hidden="true" />
                            <input
                                ref={searchRef}
                                type="text"
                                placeholder="Найти действие…"
                                aria-label="Найти действие"
                                autoComplete="off"
                                spellCheck={false}
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                            />
                            <kbd>/</kbd>
                        </div>
                        <CloseButton size="lg" aria-label="Закрыть" onClick={onClose} />
                    </header>
                    <div ref={bodyRef} className="rf-create-doc-menu__body">
                        <nav className="rf-create-doc-menu__rail" aria-label="Разделы">
                            {showQuick && renderQuickRailItem()}
                            {visibleSections.map(renderRailItem)}
                        </nav>
                        <div ref={paneRef} className="rf-create-doc-menu__pane" onScroll={onPaneScroll}>
                            {showSkeleton && renderSkeleton()}
                            {showQuick && renderQuickSection()}
                            {visibleSections.map(renderSection)}
                            {isEmpty && (
                                <div className="rf-create-doc-menu__empty">
                                    <b>Нет доступных действий</b>
                                    <span>Создание документов не настроено или недоступно вашей учётной записи.</span>
                                </div>
                            )}
                            {nothingFound && (
                                <div className="rf-create-doc-menu__empty">
                                    <b>Ничего не найдено</b>
                                    <span>
                                        Действие с таким названием недоступно или называется иначе. Попробуйте другой
                                        запрос.
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                    <footer className="rf-create-doc-menu__foot">
                        <MdOutlineVerifiedUser size={16} aria-hidden="true" />
                        <span>Показаны только разрешённые действия</span>
                        <span className="rf-create-doc-menu__foot-esc">
                            <kbd>Esc</kbd> — закрыть
                        </span>
                    </footer>
                </div>
            </FocusTrap>
        </div>,
        document.body,
    );
};

// Владелец перерисовывается и на конец загрузки действий; меню с теми же пропсами — нет
export default memo(CreateDocumentMenu);
